import React from "react";

import { formatDecimal2 } from "~/utils";
import { formatDate } from "~/utils/bloodPressureFormatters";
import { HealthBreakdown } from "../Shared";

interface BloodPressureData {
  date: string;
  systolic: number;
  diastolic: number;
}

interface HourlyBloodPressureData {
  datetime: string;
  hour: number;
  systolic: number;
  diastolic: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BloodPressureBreakdownProps {
  selectedRange: TimeRange;
  filteredData: (BloodPressureData | HourlyBloodPressureData)[];
  breakdownTitle: string;
  loading: boolean;
  error: string | null;
}

export const BloodPressureBreakdown: React.FC<BloodPressureBreakdownProps> = ({
  selectedRange,
  filteredData,
  breakdownTitle,
  loading,
  error,
}) => {
  const getKey = (item: BloodPressureData | HourlyBloodPressureData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyBloodPressureData).datetime
      : (item as BloodPressureData).date;
  };

  const getTimeDisplay = (item: BloodPressureData | HourlyBloodPressureData, selectedRange: TimeRange) => {
    return selectedRange === "1d"
      ? (item as HourlyBloodPressureData).hour + ":00"
      : formatDate((item as BloodPressureData).date, selectedRange);
  };

  const getValues = (item: BloodPressureData | HourlyBloodPressureData) => [
    {
      label: "Systolic",
      value: formatDecimal2(item.systolic),
      color: "#3b82f6", // blue-500
      unit: "mmHg",
    },
    {
      label: "Diastolic",
      value: formatDecimal2(item.diastolic),
      color: "#ef4444", // red-500
      unit: "mmHg",
    },
  ];

  return (
    <HealthBreakdown
      selectedRange={selectedRange}
      filteredData={filteredData}
      breakdownTitle={breakdownTitle}
      loading={loading}
      error={error}
      dataTypeLabel="Blood Pressure Data"
      getKey={getKey}
      getTimeDisplay={getTimeDisplay}
      getValues={getValues}
      noDataMessage="No blood pressure data available"
    />
  );
};
